import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classname from 'classnames';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getAmountCellStyles } from '../styles';
import { CellItemHeight } from '../constants';
import { getBudgetChangeString } from '../utils';

function AmountCellItem({
  value, description, className, hasBorderBottomLine, itemHeight, hasGreenColor,
}) {
  const classes = useDefaultStyles(getAmountCellStyles);
  return value ? (
    <div className={classname('amount-cell-item', className, classes.AmountItem, classes[`AmountItem-${itemHeight}`], hasBorderBottomLine && classes.BorderBottomLine)}>
      <span className={classname('amount-cell-item-value', hasGreenColor && classes.GreenColor)}>{value}</span>
      {description && <span className={classname('amount-cell-item-description', classes.Description)}>{description}</span>}
    </div>
  ) : null;
}

AmountCellItem.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  description: PropTypes.string,
  className: PropTypes.string,
  hasBorderBottomLine: PropTypes.bool,
  itemHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  hasGreenColor: PropTypes.bool,
};
AmountCellItem.defaultProps = {
  value: null,
  description: null,
  className: null,
  hasBorderBottomLine: false,
  itemHeight: null,
  hasGreenColor: false,
};

// At least one of the three values is not null: spendAmount, adjustmentAmount, budgetChangeAmount
export function AmountCell({
  i18n,
  spendAmount,
  adjustmentAmount,
  budgetChangeAmount,
  withBudgetChangeDescription,
  budgetChangeOldValue,
  budgetChangeNewValue,
  spendRawValue,
  adjustmentRawValue,
  budgetChangeRawValue,
  budgetChangeOldValueRawValue,
  budgetChangeNewValueRawValue,
}) {
  const classes = useDefaultStyles(getAmountCellStyles);
  const hasSpendUnderLine = useMemo(() => !!adjustmentAmount || !!budgetChangeAmount, [adjustmentAmount, budgetChangeAmount]);
  const hasAdjustmentUnderLine = useMemo(() => !!budgetChangeAmount, [budgetChangeAmount]);

  const budgetChangeDescription = withBudgetChangeDescription
    ? i18n.getString(
      _TL_('Budget change from {{budgetChangeOldValue}} to {{budgetChangeNewValue}}'),
      {
        budgetChangeOldValue: getBudgetChangeString({ i18n, rawValue: budgetChangeOldValueRawValue, amountValue: budgetChangeOldValue }),
        budgetChangeNewValue: getBudgetChangeString({ i18n, rawValue: budgetChangeNewValueRawValue, amountValue: budgetChangeNewValue }),
      }
    )
    : null;

  return (
    <div className={classname('amount-cell', classes.AmountCell)}>
      <AmountCellItem
        value={spendAmount}
        className="spend-amount"
        hasBorderBottomLine={hasSpendUnderLine}
        itemHeight={CellItemHeight.Small}
        hasGreenColor={Number(spendRawValue) > 0}
      />
      <AmountCellItem
        value={adjustmentAmount}
        className="adjustment-amount"
        hasBorderBottomLine={hasAdjustmentUnderLine}
        itemHeight={CellItemHeight.Small}
        hasGreenColor={Number(adjustmentRawValue) > 0}
      />
      <AmountCellItem
        value={getBudgetChangeString({
          i18n,
          rawValue: budgetChangeRawValue,
          amountValue: budgetChangeAmount,
          useEmptyString: true,
        })}
        className="budget-change-amount"
        description={budgetChangeDescription}
        itemHeight={withBudgetChangeDescription ? CellItemHeight.Large : CellItemHeight.Small}
        hasGreenColor={Number(budgetChangeRawValue) > 0}
      />
    </div>
  );
}

AmountCell.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  spendAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  adjustmentAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  budgetChangeAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  withBudgetChangeDescription: PropTypes.bool,
  budgetChangeOldValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  budgetChangeNewValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  spendRawValue: PropTypes.string,
  adjustmentRawValue: PropTypes.string,
  budgetChangeRawValue: PropTypes.string,
  budgetChangeOldValueRawValue: PropTypes.string,
  budgetChangeNewValueRawValue: PropTypes.string,
};

AmountCell.defaultProps = {
  spendAmount: null,
  adjustmentAmount: null,
  budgetChangeAmount: null,
  withBudgetChangeDescription: false,
  budgetChangeOldValue: null,
  budgetChangeNewValue: null,
  spendRawValue: null,
  adjustmentRawValue: null,
  budgetChangeRawValue: null,
  budgetChangeOldValueRawValue: null,
  budgetChangeNewValueRawValue: null,
};
